import { Caption, Switch } from '../../../../components';
import { QuestionView } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    LabelBody,
    LabelHeader4,
    LabelHeader5,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import {
    IllustrationHangingOn,
    IllustrationHoldingHands,
    IllustrationPenPaper,
    IllustrationTightRope,
    IllustrationWalking,
} from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';

const reflectionKey = 'reflection';

export const exerciseData44: ExerciseData = {
    ExerciseView: Module4Exercise4,
    dataKeys: [reflectionKey],
    questions: [
        {
            dataKeys: [reflectionKey],
            Answers(props) {
                return (
                    <QuestionView
                        {...props}
                        title="Mine fremtidsrefleksioner"
                        description={
                            <>
                                Er der noget som du vil minde dig selv om - et
                                godt tip eller andet du har fået blik for, som
                                er vigtigt at holde fast i fremadrettet?
                            </>
                        }
                    />
                );
            },
        },
    ],
};

function Module4Exercise4({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Tre tips til fremtiden" className="mt-20">
            <Switch
                cases={{
                    1: (
                        <Page1
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                })
                            }
                        />
                    ),
                    2: (
                        <Page2
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                })
                            }
                        />
                    ),
                    4: (
                        <Page4
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 5,
                                })
                            }
                        />
                    ),
                    5: (
                        <Page5
                            reflection={exercise.Data[reflectionKey] ?? ''}
                            onContinue={(reflection) =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                    data: {
                                        [reflectionKey]:
                                            normalizeInput(reflection),
                                    },
                                })
                            }
                        />
                    ),
                }}
                default={1}
                value={exercise.CurrentPage}
            />
        </Exercise>
    );
}

type PageProps = {
    onContinue: () => void;
};
function Page1({ onContinue }: PageProps) {
    return (
        <>
            <IllustrationWalking className="mx-auto" />
            <LabelHeader4 className="mt-30">
                Du er nået til vejs ende på dit forløb - så hvad nu?
            </LabelHeader4>
            <LabelBody className="mt-10">
                Du har nu været igennem øvelserne i alle fire moduler, og er ved
                vejs ende på dit forløb. Vi håber, at samtalerne med din
                psykolog og øvelserne har været hjælpsomme for det du står i.
            </LabelBody>
            <LabelBody className="mt-10">
                Vi har forsøgt at koge forløbet ind til tre pointer, som kan
                være gode at have for øje fremadrettet.
            </LabelBody>
            <LabelBody className="mt-10">
                Husk du har adgang til forløbets øvelser 6 måneder endnu, og kan
                vende tilbage når det giver mening for dig.
            </LabelBody>
            <Spacer className="mt-30" />
            <SecondaryButton onClick={onContinue}>
                Fortsæt til tippene
            </SecondaryButton>
        </>
    );
}

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <IllustrationHangingOn className="mx-auto" />
            <Caption className="mt-30">Tip #1</Caption>
            <LabelHeader4 className="mt-10">
                Pressede perioder kommer og går
            </LabelHeader4>
            <LabelBody className="mt-10">
                Det er en del af at være menneske, at føle sig presset fra tid
                til anden. Der er ingen af os, der er immune over for at føle os
                pressede. Heller ikke dig selvom du har været igennem dette
                forløb.
            </LabelBody>
            <LabelBody className="mt-10">
                Vi håber, at du i fremtiden, når du mærker presset stiger, dels
                husker, at det er okay at føle sig presset, men at det ikke må
                være i for lang tid, for så er der brug for at ændre noget. Her
                er din krop din bedste ven i at registere, om presset er ved at
                blive for stort.
            </LabelBody>
            <Spacer className="mt-30" />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

function Page3({ onContinue }: PageProps) {
    return (
        <>
            <IllustrationHoldingHands className="mx-auto" />
            <Caption className="mt-30">Tip #2</Caption>
            <LabelHeader4 className="mt-10">
                Ingen får styr på presset alene
            </LabelHeader4>
            <LabelBody className="mt-10">
                Dét at fortælle nogen om, hvordan vi har det kan ofte hjælpe i
                sig selv. Et vigtigt fremtidstip er ikke at isolere dig, når
                presset tynger, men at fortælle det til nogen. Ikke fordi de
                skal løse det for dig, men fordi ved at dele det, der er svært,
                fortynder vi det, så det bliver lettere at bære.
            </LabelBody>
            <Spacer className="mt-30" />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

function Page4({ onContinue }: PageProps) {
    return (
        <>
            <IllustrationTightRope className="mx-auto" />
            <Caption className="mt-30">Tip #3</Caption>
            <LabelHeader4 className="mt-10">
                Balance frem for selvoptimering
            </LabelHeader4>
            <LabelBody className="mt-10">
                I dag er der mange livsdomæner, som man kan føle sig målt og
                vejet på. I forsøget på at overkomme det hele kan vi hurtigt
                føle os utilstrækkelige. Her bliver den mest oplagte løsning for
                mange, at vi skal prøve hårdere og løbe endnu hurtigere, end vi
                allerede gør.
            </LabelBody>
            <LabelBody className="mt-10">
                Vi håber, at du i fremtiden, med hjælp fra dette forløb, kan
                mærke efter, om der er balance i de ting du gør, fremfor at
                optimere dig selv.
            </LabelBody>
            <LabelBody className="mt-10">
                Forsøg at skelne imellem om du bruger energi, der hvor det er
                vigtigt for dig, eller bruger du i højere grad din energi der,
                hvor du tror, det forventes af dig? Det er en forskel, der kan
                mærkes.
            </LabelBody>
            <Spacer className="mt-30" />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page5Props = {
    reflection: string;
    onContinue: (reflection: string) => void;
};
function Page5({ reflection: _reflection, onContinue }: Page5Props) {
    const [reflection, setReflection] = useSyncedState(_reflection);
    return (
        <>
            <Card>
                <IllustrationPenPaper />
                <LabelHeader5 className="mt-20">
                    Mine fremtidsrefleksioner
                </LabelHeader5>
                <LabelBody className="mt-10">
                    Det var vores tip til ting, der er vigtige at holde sig for
                    øje - men hvad mon du tænker? Er der noget som du vil minde
                    dig selv om - et godt tip eller andet du har fået blik for,
                    som er vigtigt at holde fast i fremadrettet?
                </LabelBody>
                <Textarea
                    className="my-20"
                    placeholder="Skriv her..."
                    text={reflection}
                    onChangeText={setReflection}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer className="mt-30" />
            <SecondaryButton onClick={() => onContinue(reflection)}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
